import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { tournamentApi, eventApi } from '../../../services/api';
import { useConfirm } from '../../../components/ConfirmDialog';
import toast from 'react-hot-toast';

const youthCategories = ['U9', 'U11', 'U13', 'U15', 'U17', 'U19'];

const getEventCategoryOptions = (ageCat) => {
  if (youthCategories.includes(ageCat)) {
    return <><option value="">Select</option><option value="Boys">Boys</option><option value="Girls">Girls</option><option value="Mixed">Mixed</option></>;
  }
  return <><option value="">Select</option><option value="Men">Men</option><option value="Women">Women</option><option value="Mixed">Mixed</option></>;
};

const AGE_CATEGORIES = ['U9','U11','U13','U15','U17','U19','Senior','35+','40+','45+','50+','55+','60+','65+','70+'];

const getBWFSeedInfo = (entryCount) => {
  if (entryCount < 16) return { max: 2,  label: '<16 entries → max 2 seeds' };
  if (entryCount < 32) return { max: 4,  label: '16–31 entries → max 4 seeds' };
  if (entryCount < 64) return { max: 8,  label: '32–63 entries → max 8 seeds' };
  return               { max: 16, label: '64+ entries → max 16 seeds' };
};

const exportPlayersCSV = (players, tournamentName) => {
  const headers = ['Seed', 'Name', 'Email', 'Phone', 'Age Category', 'Event Category', 'Event Type', 'Affiliation', 'Affiliation Name', 'Status'];
  const rows = players.map(p => [
    p.seed_number || '',
    p.player2_name ? `${p.player_name} / ${p.player2_name}` : (p.player_name || ''),
    p.player_email || '',
    p.player_phone || '',
    p.age_category || '',
    p.event_category || '',
    p.event_type || '',
    p.affiliation || '',
    p.affiliation_name || '',
    p.status || '',
  ]);
  const csv = [headers, ...rows].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${tournamentName || 'tournament'}_players.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const PlayersTab = ({ tournament, players, isOrganizer, onPlayersChange }) => {
  const confirm = useConfirm();
  const canAddPlayers = isOrganizer && tournament && ['draft', 'registration', 'seeding'].includes(tournament.status);
  const canEditDelete = isOrganizer && tournament && ['draft', 'registration', 'seeding'].includes(tournament.status);

  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [bulkPreviewRows, setBulkPreviewRows] = useState(null); // null = no preview yet
  const [bulkPreviewFile, setBulkPreviewFile] = useState(null);
  const [confirmingBulk, setConfirmingBulk] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [playerForm, setPlayerForm] = useState({
    player_name: '', player_email: '', age_category: '', event_category: '',
    event_type: 'Singles', seed_number: '', affiliation_name: '', affiliation_id: ''
  });
  const [addSeedOverride, setAddSeedOverride] = useState(false);
  const [editSeedValue, setEditSeedValue] = useState('');
  const [editSeedOverride, setEditSeedOverride] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deletingBulk, setDeletingBulk] = useState(false);

  // Event filter state
  const [events, setEvents] = useState([]);
  const [selectedEventFilter, setSelectedEventFilter] = useState('all');

  useEffect(() => {
    if (!tournament?.id) return;
    eventApi.list(tournament.id)
      .then(res => setEvents(res.data))
      .catch(() => {});
  }, [tournament?.id]);

  // Filtered players based on selected event
  const filteredRaw = (() => {
    if (selectedEventFilter === 'all') return players;
    if (selectedEventFilter === 'none') return players.filter(p => !p.event_id);
    return players.filter(p => p.event_id === selectedEventFilter);
  })();

  // Collapse doubles pairs (same pair_id) into a single display row.
  // The "primary" player is whichever appears first; partner is attached as _partner.
  const filteredPlayers = (() => {
    const seen = new Set();
    const result = [];
    for (const p of filteredRaw) {
      if (p.pair_id) {
        if (seen.has(p.pair_id)) continue; // already added via partner
        seen.add(p.pair_id);
        const partner = filteredRaw.find(q => q.pair_id === p.pair_id && q.id !== p.id);
        result.push({ ...p, _partner: partner || null });
      } else {
        result.push(p);
      }
    }
    return result;
  })();

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    const addBWF = getBWFSeedInfo(players.length);
    const addSeedNum = parseInt(playerForm.seed_number) || 0;
    if (addSeedNum > 0 && addSeedNum > addBWF.max && !addSeedOverride) {
      alert(`BWF Guidelines allow max ${addBWF.max} seeds for ${players.length} entries. Check "Override BWF recommendation" to proceed.`);
      return;
    }
    setAddingPlayer(true);
    try {
      const affData = { ...playerForm, affiliation: playerForm.affiliation_name ? 'Club' : 'None' };
      await tournamentApi.addPlayer(tournament.id, affData);
      setShowAddPlayer(false);
      setAddSeedOverride(false);
      setPlayerForm({ player_name: '', player_email: '', age_category: '', event_category: 'Men', event_type: 'Singles', seed_number: '', affiliation_name: '', affiliation_id: '' });
      onPlayersChange();
      alert('Player added!');
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
    finally { setAddingPlayer(false); }
  };

  const handleBulkFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    setBulkPreviewRows(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await tournamentApi.previewBulkPlayers(tournament.id, formData);
      setBulkPreviewRows(response.data.players || []);
      setBulkPreviewFile(file);
    } catch (err) { alert(err.response?.data?.error || 'Preview failed'); }
    finally { setUploadingFile(false); e.target.value = ''; }
  };

  const handleBulkConfirm = async () => {
    if (!bulkPreviewFile) return;
    setConfirmingBulk(true);
    const formData = new FormData();
    formData.append('file', bulkPreviewFile);
    try {
      const response = await tournamentApi.addPlayersBulk(tournament.id, formData);
      setShowBulkUpload(false);
      setBulkPreviewRows(null);
      setBulkPreviewFile(null);
      onPlayersChange();
      alert(response.data.message);
    } catch (err) { alert(err.response?.data?.error || 'Import failed'); }
    finally { setConfirmingBulk(false); }
  };

  const handleBulkCancel = () => {
    setShowBulkUpload(false);
    setBulkPreviewRows(null);
    setBulkPreviewFile(null);
  };

  const handleDeletePlayer = async (playerIdOrIds) => {
    const ids = Array.isArray(playerIdOrIds) ? playerIdOrIds : [playerIdOrIds];
    const isPair = ids.length > 1;
    if (!await confirm({
      title: isPair ? 'Remove Pair' : 'Remove Player',
      message: isPair ? 'Remove this doubles pair from the tournament?' : 'Remove this player from the tournament?',
      confirmLabel: 'Remove', danger: true,
    })) return;
    try {
      if (ids.length === 1) {
        await tournamentApi.deletePlayer(tournament.id, ids[0]);
      } else {
        await tournamentApi.deletePlayers(tournament.id, ids);
      }
      setSelectedIds(prev => { const next = new Set(prev); ids.forEach(id => next.delete(id)); return next; });
      onPlayersChange();
      toast.success(isPair ? 'Pair removed' : 'Player removed');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to remove'); }
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    if (!await confirm({
      title: `Remove ${ids.length} Player${ids.length > 1 ? 's' : ''}`,
      message: `Remove ${ids.length} selected player${ids.length > 1 ? 's' : ''} from the tournament? This cannot be undone.`,
      confirmLabel: 'Remove All',
      danger: true,
    })) return;
    setDeletingBulk(true);
    try {
      const res = await tournamentApi.deletePlayers(tournament.id, ids);
      setSelectedIds(new Set());
      onPlayersChange();
      toast.success(`Removed ${res.data.deleted} player${res.data.deleted !== 1 ? 's' : ''}`);
    } catch (err) { toast.error(err.response?.data?.error || 'Bulk delete failed'); }
    finally { setDeletingBulk(false); }
  };

  // Return all DB row IDs for a display row (1 for singles, 2 for a pair)
  const rowIds = useCallback((displayRow) => {
    const ids = [displayRow.id];
    if (displayRow._partner) ids.push(displayRow._partner.id);
    return ids;
  }, []);

  const toggleSelect = useCallback((displayRow) => {
    const ids = rowIds(displayRow);
    setSelectedIds(prev => {
      const next = new Set(prev);
      const isSelected = ids.every(id => next.has(id));
      ids.forEach(id => isSelected ? next.delete(id) : next.add(id));
      return next;
    });
  }, [rowIds]);

  const isRowSelected = useCallback((displayRow) => {
    const ids = rowIds(displayRow);
    return ids.every(id => selectedIds.has(id));
  }, [rowIds, selectedIds]);

  const toggleSelectAll = useCallback(() => {
    const allIds = filteredPlayers.flatMap(p => rowIds(p));
    setSelectedIds(prev =>
      prev.size === allIds.length
        ? new Set()
        : new Set(allIds)
    );
  }, [filteredPlayers, rowIds]);

  const handleUpdatePlayer = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const editBWF = getBWFSeedInfo(players.length);
    const editSeedNum = parseInt(formData.get('seed_number')) || 0;
    if (editSeedNum > 0 && editSeedNum > editBWF.max && !editSeedOverride) {
      alert(`BWF Guidelines allow max ${editBWF.max} seeds for ${players.length} entries. Check "Override BWF recommendation" to proceed.`);
      return;
    }
    const affName = formData.get('affiliation_name') || '';
    const data = {
      player_name: formData.get('player_name'),
      seed_number: formData.get('seed_number') ? parseInt(formData.get('seed_number')) : null,
      age_category: formData.get('age_category'),
      event_category: formData.get('event_category'),
      event_type: formData.get('event_type'),
      affiliation: affName ? 'Club' : 'None',
      affiliation_name: affName || null,
      affiliation_id: formData.get('affiliation_id') || null,
    };
    try {
      await tournamentApi.updatePlayer(tournament.id, editingPlayer.id, data);
      setEditingPlayer(null);
      onPlayersChange();
      alert('Updated!');
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>Registered Players</h3>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {selectedIds.size > 0 && canEditDelete && (
            <button
              className="btn btn-sm"
              style={{ background: '#dc2626', color: 'white', border: 'none' }}
              onClick={handleBulkDelete}
              disabled={deletingBulk}
            >
              {deletingBulk ? 'Removing…' : `Delete Selected (${selectedIds.size})`}
            </button>
          )}
          {filteredPlayers.length > 0 && selectedIds.size === 0 && (
            <button className="btn btn-outline btn-sm" onClick={() => exportPlayersCSV(filteredPlayers, tournament?.name)}>Export CSV</button>
          )}
          {canAddPlayers && (
            <>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAddPlayer(true)}>+ Add Player</button>
              <button className="btn btn-outline btn-sm" onClick={() => setShowBulkUpload(true)}>Upload Excel</button>
            </>
          )}
        </div>
      </div>

      {/* Event filter dropdown */}
      {events.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.82rem', color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>Filter by event:</label>
          <select
            value={selectedEventFilter}
            onChange={e => setSelectedEventFilter(e.target.value)}
            style={{ fontSize: '0.85rem', padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#111827', cursor: 'pointer' }}
          >
            <option value="all">All ({players.length})</option>
            <option value="none">No Event ({players.filter(p => !p.event_id).length})</option>
            {events.map(ev => (
              <option key={ev.id} value={ev.id}>
                {ev.name} ({players.filter(p => p.event_id === ev.id).length})
              </option>
            ))}
          </select>
        </div>
      )}

      {showAddPlayer && (
        <div className="modal-overlay" onClick={() => setShowAddPlayer(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Add Player</h3>
            <form onSubmit={handleAddPlayer}>
              <div className="form-group"><label className="form-label">Player Name *</label><input type="text" className="form-input" value={playerForm.player_name} onChange={(e) => setPlayerForm({...playerForm, player_name: e.target.value})} required /></div>
              <div className="form-group"><label className="form-label">Email</label><input type="email" className="form-input" value={playerForm.player_email} onChange={(e) => setPlayerForm({...playerForm, player_email: e.target.value})} /></div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Age Category</label>
                  <select className="form-select" value={playerForm.age_category} onChange={(e) => setPlayerForm({...playerForm, age_category: e.target.value, event_category: ''})}>
                    <option value="">Select</option>
                    {AGE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Event Category</label>
                  <select className="form-select" value={playerForm.event_category} onChange={(e) => setPlayerForm({...playerForm, event_category: e.target.value})}>
                    {getEventCategoryOptions(playerForm.age_category)}
                  </select>
                </div>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Event Type</label>
                  <select className="form-select" value={playerForm.event_type} onChange={(e) => setPlayerForm({...playerForm, event_type: e.target.value})}>
                    {(tournament.event_type?.length > 0 ? tournament.event_type : ['Singles', 'Doubles', 'Mixed Doubles']).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Seed Number</label>
                  {(() => {
                    const bwfInfo = getBWFSeedInfo(players.length);
                    const seedNum = parseInt(playerForm.seed_number) || 0;
                    const exceeds = seedNum > 0 && seedNum > bwfInfo.max;
                    return (
                      <>
                        <input
                          type="number"
                          className="form-input"
                          value={playerForm.seed_number}
                          onChange={(e) => { setPlayerForm({...playerForm, seed_number: e.target.value}); setAddSeedOverride(false); }}
                          min="1"
                          style={exceeds ? { borderColor: '#f59e0b' } : {}}
                          placeholder={`Max ${bwfInfo.max} (BWF)`}
                        />
                        {exceeds && (
                          <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '0.375rem', fontSize: '0.8rem', color: '#92400e' }}>
                            ⚠️ BWF allows max <strong>{bwfInfo.max}</strong> seeds for {players.length} entries ({bwfInfo.label}).
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.4rem', cursor: 'pointer' }}>
                              <input type="checkbox" checked={addSeedOverride} onChange={e => setAddSeedOverride(e.target.checked)} />
                              Override BWF recommendation
                            </label>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Affiliation <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: '0.8rem' }}>(optional)</span></label>
                  <input type="text" className="form-input" value={playerForm.affiliation_name}
                    onChange={(e) => setPlayerForm({...playerForm, affiliation_name: e.target.value})}
                    placeholder="Club, Academy, District, State…" />
                </div>
                <div className="form-group">
                  <label className="form-label">Affiliation ID <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: '0.8rem' }}>(optional)</span></label>
                  <input type="text" className="form-input" value={playerForm.affiliation_id}
                    onChange={(e) => setPlayerForm({...playerForm, affiliation_id: e.target.value})}
                    placeholder="BWF ID, BAI ID…" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="button" className="btn btn-outline" onClick={() => setShowAddPlayer(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={addingPlayer}>{addingPlayer ? 'Adding...' : 'Add Player'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulkUpload && (
        <div className="modal-overlay" onClick={!uploadingFile && !confirmingBulk ? handleBulkCancel : undefined}>
          <div className="modal" style={{ maxWidth: bulkPreviewRows ? '800px' : '480px', width: '95%' }} onClick={e => e.stopPropagation()}>
            <h3>Upload Players from Excel</h3>

            {!bulkPreviewRows ? (
              <>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
                  Upload an Excel or CSV file. We'll preview how each row matches existing player profiles before importing.
                </p>
                <input type="file" accept=".xlsx,.xls,.csv" onChange={handleBulkFileSelect} disabled={uploadingFile} />
                {uploadingFile && <p style={{ marginTop: '0.5rem', color: '#6b7280', fontSize: '0.85rem' }}>Analyzing file...</p>}
                <div style={{ marginTop: '1rem', textAlign: 'right' }}>
                  <button className="btn btn-outline" onClick={handleBulkCancel}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '0.75rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.8rem' }}>
                  {[
                    { color: '#d1fae5', border: '#6ee7b7', label: 'Matched existing profile' },
                    { color: '#fef3c7', border: '#fcd34d', label: 'Possible match — will link' },
                    { color: '#dbeafe', border: '#93c5fd', label: 'New profile will be created' },
                    { color: '#fee2e2', border: '#fca5a5', label: 'Error / skipped' },
                  ].map(({ color, border, label }) => (
                    <span key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <span style={{ width: 14, height: 14, background: color, border: `1px solid ${border}`, borderRadius: 3, display: 'inline-block' }} />
                      {label}
                    </span>
                  ))}
                </div>
                <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                  <table className="table" style={{ fontSize: '0.8rem', margin: 0 }}>
                    <thead>
                      <tr><th>#</th><th>Name</th><th>Email</th><th>Age Cat</th><th>Event Cat</th><th>Profile Match</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {bulkPreviewRows.map((row, i) => {
                        const bg =
                          row.match_status === 'matched'        ? '#d1fae5' :
                          row.match_status === 'possible_match' ? '#fef3c7' :
                          row.match_status === 'error'          ? '#fee2e2' :
                          '#dbeafe';
                        return (
                          <tr key={i} style={{ background: bg }}>
                            <td>{i + 1}</td>
                            <td>{row.player_name || '—'}</td>
                            <td style={{ fontSize: '0.75rem' }}>{row.player_email || '—'}</td>
                            <td>{row.age_category || '—'}</td>
                            <td>{row.event_category || '—'}</td>
                            <td style={{ fontSize: '0.75rem' }}>
                              {row.match_status === 'matched' && <span style={{ color: '#065f46' }}>Linked: {row.matched_name || 'profile'}</span>}
                              {row.match_status === 'possible_match' && <span style={{ color: '#78350f' }}>Possible: {row.matched_name || '?'}</span>}
                              {row.match_status === 'new' && <span style={{ color: '#1e40af' }}>New profile</span>}
                              {row.match_status === 'error' && <span style={{ color: '#991b1b' }}>{row.error || 'Error'}</span>}
                            </td>
                            <td>
                              <span style={{
                                fontSize: '0.72rem', padding: '1px 6px', borderRadius: 3, fontWeight: 500,
                                background: row.match_status === 'matched' ? '#065f46' : row.match_status === 'error' ? '#991b1b' : '#1e40af',
                                color: 'white'
                              }}>
                                {row.match_status || 'new'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0.75rem 0' }}>
                  {bulkPreviewRows.filter(r => r.match_status !== 'error').length} of {bulkPreviewRows.length} rows will be imported.
                  {bulkPreviewRows.filter(r => r.match_status === 'error').length > 0 && ` ${bulkPreviewRows.filter(r => r.match_status === 'error').length} rows with errors will be skipped.`}
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button className="btn btn-outline" onClick={() => { setBulkPreviewRows(null); setBulkPreviewFile(null); }}>Back</button>
                  <button className="btn btn-outline" onClick={handleBulkCancel}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleBulkConfirm} disabled={confirmingBulk || bulkPreviewRows.every(r => r.match_status === 'error')}>
                    {confirmingBulk ? 'Importing...' : `Import ${bulkPreviewRows.filter(r => r.match_status !== 'error').length} Players`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {editingPlayer && (
        <div className="modal-overlay" onClick={() => setEditingPlayer(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Edit Player</h3>
            <form onSubmit={handleUpdatePlayer}>
              <div className="form-group"><label className="form-label">Player Name *</label><input type="text" name="player_name" className="form-input" defaultValue={editingPlayer.player_name || ''} required /></div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Age Category</label>
                  <select name="age_category" className="form-select" defaultValue={editingPlayer.age_category || ''}>
                    <option value="">Select</option>
                    {AGE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Event Category</label>
                  <select name="event_category" className="form-select" defaultValue={editingPlayer.event_category || ''}>
                    {getEventCategoryOptions(editingPlayer.age_category)}
                  </select>
                </div>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Event Type</label>
                  <select name="event_type" className="form-select" defaultValue={editingPlayer.event_type || 'Singles'}>
                    {(tournament.event_type?.length > 0 ? tournament.event_type : ['Singles', 'Doubles', 'Mixed Doubles']).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Seed Number</label>
                  {(() => {
                    const bwfInfo = getBWFSeedInfo(players.length);
                    const seedNum = parseInt(editSeedValue) || 0;
                    const exceeds = seedNum > 0 && seedNum > bwfInfo.max;
                    return (
                      <>
                        <input
                          type="number"
                          name="seed_number"
                          className="form-input"
                          value={editSeedValue}
                          onChange={(e) => { setEditSeedValue(e.target.value); setEditSeedOverride(false); }}
                          min="1"
                          style={exceeds ? { borderColor: '#f59e0b' } : {}}
                          placeholder={`Max ${bwfInfo.max} (BWF)`}
                        />
                        {exceeds && (
                          <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '0.375rem', fontSize: '0.8rem', color: '#92400e' }}>
                            ⚠️ BWF allows max <strong>{bwfInfo.max}</strong> seeds for {players.length} entries ({bwfInfo.label}).
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.4rem', cursor: 'pointer' }}>
                              <input type="checkbox" checked={editSeedOverride} onChange={e => setEditSeedOverride(e.target.checked)} />
                              Override BWF recommendation
                            </label>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Affiliation <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: '0.8rem' }}>(optional)</span></label>
                  <input type="text" name="affiliation_name" className="form-input"
                    defaultValue={editingPlayer.affiliation_name || ''}
                    placeholder="Club, Academy, District, State…" />
                </div>
                <div className="form-group">
                  <label className="form-label">Affiliation ID <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: '0.8rem' }}>(optional)</span></label>
                  <input type="text" name="affiliation_id" className="form-input"
                    defaultValue={editingPlayer.affiliation_id || ''}
                    placeholder="BWF ID, BAI ID…" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="button" className="btn btn-outline" onClick={() => setEditingPlayer(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {filteredPlayers.length === 0 ? (
        <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
          {players.length === 0 ? 'No players registered yet' : 'No players match the selected filter'}
        </p>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                {canEditDelete && (
                  <th style={{ width: 36, textAlign: 'center' }}>
                    {(() => {
                      const allIds = filteredPlayers.flatMap(p => rowIds(p));
                      return (
                        <input
                          type="checkbox"
                          checked={allIds.length > 0 && allIds.every(id => selectedIds.has(id))}
                          ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && !allIds.every(id => selectedIds.has(id)); }}
                          onChange={toggleSelectAll}
                        />
                      );
                    })()}
                  </th>
                )}
                <th>Seed</th><th>Name</th><th>Email</th><th>Age Cat</th><th>Event Cat</th><th>Event</th><th>Affiliation</th>
                {events.length > 0 && <th>Registered For</th>}
                <th>Status</th>
                {canEditDelete && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredPlayers.map((player) => {
                const playerEvent = events.find(ev => ev.id === player.event_id);
                const isPair      = !!player._partner;
                const selected    = isRowSelected(player);

                // Build display name: "P1 / P2" for pairs, just player_name for singles
                const displayName = isPair
                  ? `${player.player_name} / ${player._partner.player_name}`
                  : (player.player2_name
                      ? `${player.player_name} / ${player.player2_name}` // legacy single-row doubles
                      : (player.player_name || 'TBD'));

                return (
                  <tr key={player.id} style={selected ? { background: '#F0FDFA' } : {}}>
                    {canEditDelete && (
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelect(player)}
                        />
                      </td>
                    )}
                    <td>{player.seed_number || '-'}</td>
                    <td style={{ fontWeight: 500 }}>
                      {isPair ? (
                        <>
                          {player.profile_id
                            ? <Link to={`/profiles/${player.profile_id}`} style={{ color: '#0D9488', textDecoration: 'none' }}>{player.player_name}</Link>
                            : player.player_name}
                          {' / '}
                          {player._partner.profile_id
                            ? <Link to={`/profiles/${player._partner.profile_id}`} style={{ color: '#0D9488', textDecoration: 'none' }}>{player._partner.player_name}</Link>
                            : player._partner.player_name}
                        </>
                      ) : player.player2_name ? (
                        // Legacy single-row doubles
                        <>
                          {player.profile_id
                            ? <Link to={`/profiles/${player.profile_id}`} style={{ color: '#0D9488', textDecoration: 'none' }}>{player.player_name}</Link>
                            : player.player_name}
                          {' / '}
                          {player.player2_name}
                        </>
                      ) : (
                        player.profile_id
                          ? <Link to={`/profiles/${player.profile_id}`} style={{ color: '#0D9488', textDecoration: 'none' }}>{displayName}</Link>
                          : displayName
                      )}
                    </td>
                    <td>{player.player_email || '-'}</td>
                    <td>{player.age_category || '-'}</td>
                    <td>{player.event_category || '-'}</td>
                    <td>{player.event_type || '-'}</td>
                    <td style={{ fontSize: '0.82rem', color: '#6b7280' }}>
                      {isPair
                        ? [player.affiliation_name, player._partner.affiliation_name].filter(Boolean).join(' / ') || '—'
                        : (player.affiliation_name || '—')}
                    </td>
                    {events.length > 0 && (
                      <td>
                        {playerEvent
                          ? <span style={{ fontSize: '0.78rem', background: '#F0FDFA', color: '#0D9488', padding: '2px 7px', borderRadius: 4, fontWeight: 500 }}>{playerEvent.name}</span>
                          : <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>—</span>
                        }
                      </td>
                    )}
                    <td><span className={`badge ${player.status === 'registered' ? 'badge-primary' : 'badge-success'}`}>{player.status}</span></td>
                    {canEditDelete && (
                      <td>
                        {!isPair && (
                          <button className="btn btn-sm btn-outline" onClick={() => { setEditingPlayer({ ...player }); setEditSeedValue(player.seed_number || ''); setEditSeedOverride(false); }}>✏️</button>
                        )}
                        <button className="btn btn-sm btn-outline" onClick={() => handleDeletePlayer(isPair ? rowIds(player) : player.id)}>🗑️</button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PlayersTab;
